import { hideableValueGetValue, hideableValueIsVisible, doppeValidateAppleMusicURL } from "@wix/devzai-utils-common";
import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
} from '../../client-server-common';
import type {RichTextContent, Values, HideableValue } from '@wix/devzai-utils-common';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';


export const AppleMusicWidgetSize = {
    Normal: 'Normal',
    Compact: 'Compact',
} as const;

export namespace DoppeActionTypeAppleMusic {

    export type Size = Values<typeof AppleMusicWidgetSize>;

    export interface ActionSettings extends
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
        url: string;
        size: Size;
        title: HideableValue<string>;
        description: HideableValue<RichTextContent>;
    }

    export type DeprecatedFields = Partial<DoppeActionCtaButtonSettings> & {
        buttonText?: HideableValue<string>;
    }

    export type PartialProps = Partial<ActionSettings> & DeprecatedFields;
}

export const doppeActionTypeAppleMusic = doppeActionTypeDefineMetadata<DoppeActionTypeAppleMusic.ActionSettings, DoppeActionTypeAppleMusic.PartialProps>({
    id: 'io.bymo.action/apple-music',
    resolveActionSettings: action => {

        const {
            buttonText = '',
            ctaButton,
            ctaButtons,
            sections,
            ...restSettings
        } = action.settings;

        return {
            url: '',
            buttonText: buttonText,
            title: 'Title',
            description: 'Description',
            size: AppleMusicWidgetSize.Compact,
            ...getDefaultDoppeActionCtaButtonSettings({
                buttonText: hideableValueGetValue(buttonText),
                showButton: hideableValueIsVisible(buttonText),
            }),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                hasSourceURL: true,
                ctaButton: ctaButton,
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, true),
            ...restSettings
        }

    },
    hasNoGoalMeasuring: false,
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            url: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl, {
                description: 'urlFormat.appleMusic',
                aiValidator: (value) => {
                    if (typeof value !== 'string') {
                        return `Expected a URL string, got ${typeof value}`;
                    }
                    if (!doppeValidateAppleMusicURL(value)) {
                        return `Must be an Apple Music URL starting with https://music.apple.com/ or https://podcasts.apple.com/`;
                    }
                    return null;
                },
            }),
            title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                description: true,
                title: true
            }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    hasNoLockInnerCTA: true,
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: actionSettings.url ?? null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.url = '';
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
})

